import {
  reduceSchedulesIntoCategories,
  getCenterDescription,
} from '../../utils/schools/schoolFunctions';

const getTypeOfLanguage = (languages) => {
  const qtyPrimaryLanguages = languages.reduce((accLanguages, crrLanguage) => {
    let acc = accLanguages;
    // eslint-disable-next-line no-return-assign
    if (crrLanguage.name) return acc += 1;
    return acc;
  }, 0);
  const languageMap = {
    0: '',
    1: 'Lengua Primaria',
    2: 'Bilingüe',
    3: 'Trilingüe',
  };
  return languageMap[qtyPrimaryLanguages];
};

// Data transformations
export const transformSchoolData = (rawData) => {
  const normalizedData = {
    ...rawData,
    miniCard: {
      name: rawData.name,
      address: rawData.address,
      logo: rawData.logo,
      gallery_pictures: rawData.gallery_pictures,
    },
    description: {
      text: rawData.description,
      mission: rawData.mission,
      vision: rawData.vision,
      values: rawData.values,
    },
  };

  return normalizedData;
};

// Selector functions that work directly with schoolData
export const getMiniCardData = (schoolData) => {
  if (!schoolData) return {};

  const miniCardKeys = {
    city: schoolData.city,
    neighborhood: schoolData.neighborhood,
    name: schoolData.name,
    address: schoolData.address,
    logo: schoolData.logo,
    latitude: schoolData.latitude,
    longitude: schoolData.longitude,
    schedules: schoolData.schedules,
    countryId: schoolData.countryId,
    verified: schoolData.verified_by_institution,
    rating: schoolData.rating,
    userRatingsQty: schoolData.user_ratings_total,
    schoolManagement: schoolData.public,
    schoolAffinity: schoolData.schoolAffinity,
    postulate: schoolData.postulate,
    website: schoolData.website,
    placeId: schoolData.place_id,
  };

  return {
    ...miniCardKeys,
    centerDescription: getCenterDescription(
      miniCardKeys.name,
      miniCardKeys.schedules,
      miniCardKeys.countryId,
    ),
  };
};

export const getLoadingState = (loading) => loading === 'pending';

export const getDescriptionData = (schoolData) => schoolData.description;

const getAvailableLanguages = (languages, foreignLanguages) => {
  let availableLanguages = languages.reduce((accLanguages, crrLanguage) => {
    if (crrLanguage.name) {
      return [...accLanguages, {
        ...crrLanguage,
        multiLingualType: getTypeOfLanguage(languages),
      }];
    }
    return accLanguages;
  }, []);

  availableLanguages = [...availableLanguages].concat(foreignLanguages);
  if (availableLanguages.length > 6) return availableLanguages.slice(0, 6);
  return availableLanguages;
};

const getTooltip = (isPrimary) => {
  if (isPrimary) return 'Una parte o todas las materias se dictan en este idioma, procurando dominio total, oral y escrito.';
  return 'Se dictan determinadas horas semanales de forma regular.';
};

const mutateForeignLanguages = (foreignLanguages) => foreignLanguages.map((lng) => ({
  ...lng,
  name: lng.lang.name,
  multiLingualType: 'Lengua extranjera',
  icon: lng.lang.icon,
}));

const mutateLanguages = (languages) => languages.map((lng) => ({
  ...lng,
  tooltip: getTooltip(lng.isPrimary),
  icon: lng.icon,
  isPrimary: lng.isPrimary || false,

}));

const selectLanguageData = (state) => {
  const foreignLanguages = state.languages;
  const mutatedForeignLanguages = mutateForeignLanguages(foreignLanguages);

  const primaryLanguages = [
    {
      ...state.language1,
      isPrimary: true,
    },
    {
      ...state.language2,
      isPrimary: true,
    },
    {
      ...state.language3,
      isPrimary: true,
    },
  ];
  const availableLanguages = getAvailableLanguages(primaryLanguages, mutatedForeignLanguages);
  const mutatedLanguages = mutateLanguages(availableLanguages);
  return mutatedLanguages;
};

export const getLanguageData = (schoolData) => ({
  languages: selectLanguageData(schoolData),
  language_description: schoolData.language_description,
});

export const getLanguageDescription = (schoolData) => schoolData.language_description;

export const getMainFeaturesData = (schoolData) => {
  if (!schoolData) return { features: [] };

  const features = [
    {
      name: getTypeOfLanguage([schoolData.language1, schoolData.language2, schoolData.language3]),
      title: 'Idiomas',
      icon: 'https://images.vacanted.com.uy/school-icons/idiomas.svg',
    },
    {
      name: schoolData.religious ? schoolData.religion?.name : 'Institución Laica',
      title: 'Religión',
      icon: schoolData.religion?.icon,
    },
    {
      name: schoolData.genre?.name,
      title: 'Tipo de Institución',
      icon: schoolData.genre?.icon,
    },
    {
      name: `Zona ${schoolData.urban?.name === 'Urbano' ? 'Urbana' : schoolData.urban?.name}`,
      title: 'Ubicación',
      icon: schoolData.urban?.icon,
    },
    {
      name: schoolData.cost?.id === 3 ? `De ${schoolData.cost?.name}` : schoolData.cost?.name,
      title: 'Costo',
      icon: schoolData.cost?.icon,
    },
    {
      name: schoolData.public?.name,
      title: 'Gestión Educativa',
      icon: schoolData.public?.icon,
    },
  ];

  return {
    features,
    features_description: schoolData.features_description,
  };
};

export const getStepsData = (schoolData) => (
  schoolData.schedules ? schoolData.schedules.reduce(reduceSchedulesIntoCategories, []) : []
);

export const getSchedulesData = (schoolData) => schoolData.schedules;

export const getActivityData = (schoolData) => schoolData.schoolactivities;

export const getCoords = (schoolData) => ({
  lat: schoolData.latitude,
  lng: schoolData.longitude,
});

export const getSchoolInfo = (schoolData) => ({
  schoolName: schoolData.name,
  schoolManagement: schoolData.public_id,
  calendar: schoolData.calendar,
  schoolAffinity: schoolData.schoolAffinity,
  postulate: schoolData.postulate,
  website: schoolData.website,
  contactDisabled: schoolData.contact_disabled,
});

export const getReligionData = (schoolData) => ({
  religionName: schoolData.religion?.name,
  religionDescription: schoolData.religious_description,
});

export const getEducationalLevelsData = (schoolData) => ({
  activities: schoolData.schoolactivities || [],
  description: schoolData.educational_levels_description,
});

export const getGoogleReviewsData = (schoolData) => ({
  placeId: schoolData.place_id,
});
